import { Component, OnInit } from '@angular/core';
import { Chart } from 'chart.js';
import { ReqResService } from '../../service/req-res.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-infdefchart',
  templateUrl: './infdefchart.component.html',
  styleUrls: ['./infdefchart.component.css']
})
export class InfdefchartComponent implements OnInit {

  begDate: string = '';
  endDate: string = '';

  changedItems: number = 0;
  inflationCount: number = 0;
  deflationCount: number = 0;
  avgInflation: number = 0;
  avgDeflation: number = 0;

  detailData: any[] = [];
  rawData: any[] = [];
  loading: boolean = false;

  overallChart: any = null;
  itemChart: any = null;

  selectedItem: any = null;
  selectedItemHistory: any[] = [];

  constructor(private reqres: ReqResService) { }

  ngOnInit(): void {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    this.begDate = this.formatDate(firstDay);
    this.endDate = this.formatDate(today);

    this.getData();
  }

  formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = ('0' + (date.getMonth() + 1)).slice(-2);
    const d = ('0' + date.getDate()).slice(-2);
    return `${y}-${m}-${d}`;
  }

  normalizePrice(value: any): number {
    const price = Number(value) || 0;
    return Math.round((price + Number.EPSILON) * 100) / 100;
  }

  getData(): void {
    if (!this.begDate || !this.endDate) {
      return;
    }

    this.loading = true;
    this.selectedItem = null;
    this.selectedItemHistory = [];

    const url = `Report/GetInfDefChart?beg=${this.begDate}&end=${this.endDate}`;

    this.reqres.getData(url).subscribe({
      next: (res: any) => {
        this.loading = false;

        const data = typeof res === 'string' ? JSON.parse(res) : res;
        this.rawData = Array.isArray(data) ? data : [];
        this.calculateSummaryFromRawData();

        setTimeout(() => {
          this.renderOverallChart();
          this.renderAllItemsChart();
        }, 0);
      },
      error: (err: any) => {
        this.loading = false;
        console.error(err);
        this.rawData = [];
        this.detailData = [];
        this.selectedItem = null;
        this.selectedItemHistory = [];
        this.destroyCharts();
      }
    });
  }

  groupAverageByItemAndDate(rows: any[]): any[] {
    const grouped: any = {};

    rows.forEach((r: any) => {
      const key = `${r.ItemId}_${r.orderDate}`;

      if (!grouped[key]) {
        grouped[key] = {
          ItemId: r.ItemId,
          ItemCode: r.ItemCode,
          ItemName: r.ItemName,
          orderDate: r.orderDate,
          prices: []
        };
      }

      grouped[key].prices.push(this.normalizePrice(r.Price));
    });

    return Object.values(grouped).map((g: any) => ({
      ItemId: g.ItemId,
      ItemCode: g.ItemCode,
      ItemName: g.ItemName,
      orderDate: g.orderDate,
      Price: this.normalizePrice(g.prices.reduce((a: number, b: number) => a + b, 0) / g.prices.length)
    }));
  }

  removeConsecutiveDuplicates(history: any[]): any[] {
    const result: any[] = [];

    for (let i = 0; i < history.length; i++) {
      if (i === 0 || this.normalizePrice(history[i].price) !== this.normalizePrice(history[i - 1].price)) {
        result.push(history[i]);
      }
    }

    return result;
  }

  calculateSummaryFromRawData(): void {
    const rowsInRange = this.rawData.filter((x: any) => x.orderDate >= this.begDate && x.orderDate <= this.endDate);

    if (!rowsInRange.length) {
      this.changedItems = 0;
      this.inflationCount = 0;
      this.deflationCount = 0;
      this.avgInflation = 0;
      this.avgDeflation = 0;
      this.detailData = [];
      return;
    }

    const averagedRows = this.groupAverageByItemAndDate(rowsInRange);
    const itemMap: any = {};

    averagedRows.forEach((r: any) => {
      if (!itemMap[r.ItemId]) {
        itemMap[r.ItemId] = {
          ItemId: r.ItemId,
          ItemCode: r.ItemCode,
          ItemName: r.ItemName,
          History: []
        };
      }

      itemMap[r.ItemId].History.push({
        orderDate: r.orderDate,
        price: this.normalizePrice(r.Price)
      });
    });

    const results = Object.values(itemMap).map((item: any) => {
      item.History.sort((a: any, b: any) => a.orderDate.localeCompare(b.orderDate));

      const filteredHistory = this.removeConsecutiveDuplicates(item.History);
      const first = filteredHistory[0];
      const last = filteredHistory[filteredHistory.length - 1];

      const fromPrice = this.normalizePrice(first.price);
      const toPrice = this.normalizePrice(last.price);
      const diff = this.normalizePrice(toPrice - fromPrice);

      let changePercent: number | null = 0;

      if (fromPrice !== 0) {
        changePercent = (diff / fromPrice) * 100;
      } else if (fromPrice === 0 && toPrice === 0) {
        changePercent = 0;
      } else {
        changePercent = null;
      }

      let status = 'No Change';
      if (diff > 0) status = 'Inflation';
      if (diff < 0) status = 'Deflation';

      return {
        itemId: item.ItemId,
        itemCode: item.ItemCode,
        itemName: item.ItemName,
        fromPrice: fromPrice,
        toPrice: toPrice,
        difference: diff,
        changePercent: changePercent,
        status: status,
        history: filteredHistory
      };
    }).filter((x: any) => x.status !== 'No Change');

    results.sort((a: any, b: any) => {
      const aVal = a.changePercent === null ? -999999 : Math.abs(a.changePercent);
      const bVal = b.changePercent === null ? -999999 : Math.abs(b.changePercent);
      return bVal - aVal;
    });

    const inflationItems = results.filter((x: any) => x.status === 'Inflation');
    const deflationItems = results.filter((x: any) => x.status === 'Deflation');

    const validInflationPercents = inflationItems
      .filter((x: any) => x.changePercent !== null)
      .map((x: any) => Math.abs(x.changePercent));

    const validDeflationPercents = deflationItems
      .filter((x: any) => x.changePercent !== null)
      .map((x: any) => Math.abs(x.changePercent));

    this.changedItems = results.length;
    this.inflationCount = inflationItems.length;
    this.deflationCount = deflationItems.length;

    this.avgInflation = validInflationPercents.length
      ? validInflationPercents.reduce((a: number, b: number) => a + b, 0) / validInflationPercents.length
      : 0;

    this.avgDeflation = validDeflationPercents.length
      ? validDeflationPercents.reduce((a: number, b: number) => a + b, 0) / validDeflationPercents.length
      : 0;

    this.detailData = results;
  }

  renderOverallChart(): void {
    if (this.overallChart) {
      this.overallChart.destroy();
      this.overallChart = null;
    }

    const canvas: any = document.getElementById('overallChart');
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    this.overallChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Inflation', 'Deflation'],
        datasets: [
          {
            label: 'Item Count',
            data: [this.inflationCount, this.deflationCount],
            backgroundColor: ['rgba(220, 38, 38, 0.75)', 'rgba(22, 163, 74, 0.75)'],
            borderColor: ['#dc2626', '#16a34a'],
            borderWidth: 1,
            yAxisID: 'y-axis-count'
          },
          {
            label: 'Average Change %',
            data: [this.avgInflation, this.avgDeflation],
            type: 'line',
            fill: false,
            borderColor: '#111827',
            backgroundColor: '#111827',
            borderWidth: 2,
            lineTension: 0.25,
            pointRadius: 4,
            pointHoverRadius: 6,
            yAxisID: 'y-axis-percent'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        title: {
          display: true,
          text: `Overall Summary (${this.begDate} → ${this.endDate})`
        },
        legend: {
          display: true
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false
              }
            }
          ],
          yAxes: [
            {
              id: 'y-axis-count',
              type: 'linear',
              position: 'left',
              ticks: {
                beginAtZero: true
              },
              gridLines: {
                color: '#edf0f4'
              }
            },
            {
              id: 'y-axis-percent',
              type: 'linear',
              position: 'right',
              ticks: {
                beginAtZero: true,
                callback: function (value: any) {
                  return value + '%';
                }
              },
              gridLines: {
                drawOnChartArea: false
              }
            }
          ]
        }
      }
    });
  }

  renderAllItemsChart(): void {
    if (this.itemChart) {
      this.itemChart.destroy();
      this.itemChart = null;
    }

    const canvas: any = document.getElementById('itemChart');
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const maxSeries = 12;
    const chartItems = this.detailData.slice(0, maxSeries);
    const allDates = this.getAllDatesFromItems(chartItems);

    const datasets = chartItems.map((item: any, index: number) => {
      const color = this.getSeriesColor(index, item.status);
      const priceMap: any = {};

      item.history.forEach((h: any) => {
        priceMap[h.orderDate] = h.price;
      });

      const data = allDates.map((date: string) => {
        return priceMap[date] !== undefined ? priceMap[date] : null;
      });

      return {
        label: `${item.itemCode}`,
        data: data,
        borderColor: color,
        backgroundColor: color,
        fill: false,
        borderWidth: 2,
        lineTension: 0.2,
        pointRadius: 2,
        pointHoverRadius: 4,
        spanGaps: true
      };
    });

    this.itemChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: allDates,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: 'bottom'
        },
        tooltips: {
          mode: 'nearest',
          intersect: false
        },
        hover: {
          mode: 'nearest',
          intersect: false
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false
              },
              scaleLabel: {
                display: true,
                labelString: 'Date'
              }
            }
          ],
          yAxes: [
            {
              ticks: {
                beginAtZero: false
              },
              gridLines: {
                color: '#edf0f4'
              },
              scaleLabel: {
                display: true,
                labelString: 'Price'
              }
            }
          ]
        }
      }
    });
  }

  renderItemChart(item: any): void {
    if (this.itemChart) {
      this.itemChart.destroy();
      this.itemChart = null;
    }

    const canvas: any = document.getElementById('itemChart');
    if (!canvas || !item || !item.history || !item.history.length) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const labels = item.history.map((x: any) => x.orderDate);
    const values = item.history.map((x: any) => this.normalizePrice(x.price));
    const chartColor = item.status === 'Inflation' ? '#dc2626' : '#16a34a';

    this.itemChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: `${item.itemCode} - ${item.itemName}`,
            data: values,
            borderColor: chartColor,
            backgroundColor: chartColor,
            fill: false,
            borderWidth: 3,
            lineTension: 0.25,
            pointRadius: 4,
            pointHoverRadius: 6
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false
              },
              scaleLabel: {
                display: true,
                labelString: 'Date'
              }
            }
          ],
          yAxes: [
            {
              ticks: {
                beginAtZero: false
              },
              gridLines: {
                color: '#edf0f4'
              },
              scaleLabel: {
                display: true,
                labelString: 'Price'
              }
            }
          ]
        }
      }
    });
  }

  getAllDatesFromItems(items: any[]): string[] {
    const dateSet = new Set<string>();

    items.forEach((item: any) => {
      item.history.forEach((h: any) => dateSet.add(h.orderDate));
    });

    return Array.from(dateSet).sort();
  }

  getSeriesColor(index: number, status: string): string {
    const redPalette = ['#dc2626', '#ef4444', '#b91c1c', '#f87171', '#991b1b', '#fb7185'];
    const greenPalette = ['#16a34a', '#22c55e', '#15803d', '#4ade80', '#166534', '#86efac'];

    if (status === 'Inflation') {
      return redPalette[index % redPalette.length];
    }

    return greenPalette[index % greenPalette.length];
  }

  selectItem(item: any): void {
    this.selectedItem = item;
    this.buildSelectedItemHistory(item);

    setTimeout(() => {
      this.renderItemChart(item);
    }, 0);
  }

  showAllOverview(): void {
    this.selectedItem = null;
    this.selectedItemHistory = [];

    setTimeout(() => {
      this.renderAllItemsChart();
    }, 0);
  }

  buildSelectedItemHistory(item: any): void {
    if (!item || !item.history) {
      this.selectedItemHistory = [];
      return;
    }

    const history = [...item.history].sort((a: any, b: any) => a.orderDate.localeCompare(b.orderDate));

    this.selectedItemHistory = history.map((h: any, index: number) => {
      const previous = index > 0 ? history[index - 1] : null;
      const currentPrice = this.normalizePrice(h.price);
      const previousPrice = previous ? this.normalizePrice(previous.price) : null;

      let diff: number | null = null;
      let changePercent: number | null = null;
      let rowStatus = 'No Change';

      if (previous) {
        diff = this.normalizePrice(currentPrice - (previousPrice || 0));

        if ((previousPrice || 0) !== 0) {
          changePercent = (diff / (previousPrice || 0)) * 100;
        } else if ((previousPrice || 0) === 0 && currentPrice === 0) {
          changePercent = 0;
        } else {
          changePercent = null;
        }

        if (diff > 0) rowStatus = 'Inflation';
        if (diff < 0) rowStatus = 'Deflation';
      }

      return {
        orderDate: h.orderDate,
        price: currentPrice,
        previousPrice: previousPrice,
        diff: diff,
        changePercent: changePercent,
        rowStatus: rowStatus
      };
    });
  }

  exportCurrentTableToExcel(): void {
    let exportData: any[] = [];
    let fileName = 'changed_items.xlsx';

    if (this.selectedItem) {
      exportData = this.selectedItemHistory.map((x: any, index: number) => ({
        No: index + 1,
        Date: x.orderDate,
        Price: x.price,
        PreviousPrice: x.previousPrice == null ? '' : x.previousPrice,
        Difference: x.diff == null ? '' : x.diff,
        ChangePercent: x.changePercent == null ? '' : x.changePercent,
        Status: x.rowStatus
      }));

      fileName = `${this.selectedItem.itemCode}_history.xlsx`;
    } else {
      exportData = this.detailData.map((x: any, index: number) => ({
        No: index + 1,
        Code: x.itemCode,
        Name: x.itemName,
        FirstPrice: x.fromPrice,
        LastPrice: x.toPrice,
        Difference: x.difference,
        ChangePercent: x.changePercent,
        Status: x.status,
        History: x.history.map((h: any) => `${h.orderDate} - ${Number(h.price).toFixed(2)}`).join(' | ')
      }));

      fileName = `overall_changed_items_${this.begDate}_${this.endDate}.xlsx`;
    }

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
    const workbook: XLSX.WorkBook = {
      Sheets: { Data: worksheet },
      SheetNames: ['Data']
    };

    XLSX.writeFile(workbook, fileName);
  }

  destroyCharts(): void {
    if (this.overallChart) {
      this.overallChart.destroy();
      this.overallChart = null;
    }

    if (this.itemChart) {
      this.itemChart.destroy();
      this.itemChart = null;
    }
  }

  getChangeClass(item: any): string {
    return item.status === 'Inflation' ? 'change-up' : 'change-down';
  }

  getBadgeClass(item: any): string {
    return item.status === 'Inflation' ? 'inflation' : 'deflation';
  }

  getArrow(item: any): string {
    return item.status === 'Inflation' ? '▲' : '▼';
  }
}
